import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar';
import fetchWithToken, { getUserData } from '../../services';
import { useUserCountry, useSetUserCountry, useSetUserIcon, useHasStripeAccount, useSetHasStripeAccount, useStripeOnboardingStatus, useSetStripeOnboardingStatus, useSignupMethod, useSetSignupMethod } from '../../state';
import ProfileSection from './ProfileSection';
import PasswordSection from './PasswordSection';
import BankDetailsSection from './BankDetailsSection';
import DeleteAccountSection from './DeleteAccountSection';

const Settings = () => {
  const userCountry = useUserCountry();
  const setUserCountry = useSetUserCountry();
  const setUserIcon = useSetUserIcon();
  const hasStripeAccount = useHasStripeAccount();
  const setHasStripeAccount = useSetHasStripeAccount();
  const stripeOnboardingStatus = useStripeOnboardingStatus();
  const setStripeOnboardingStatus = useSetStripeOnboardingStatus();
  const signupMethod = useSignupMethod();
  const setSignupMethod = useSetSignupMethod();
  console.log('stripe onboarding status: ', stripeOnboardingStatus);
  const location = useLocation();

  // Current form values
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [icon, setIcon] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [linkId, setLinkId] = useState('');

  // Original values from the server
  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [originalIcon, setOriginalIcon] = useState('');
  const [originalLinkId, setOriginalLinkId] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ text: '', type: '' });
  const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });
  const [bankDetailsMessage, setBankDetailsMessage] = useState({ text: '', type: '' });
  const [isLoadingBankDetails, setIsLoadingBankDetails] = useState(false);

  // Check URL parameters on component mount to detect Stripe onboarding/update completion
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const stripeOnboardingComplete = queryParams.get('stripe_onboarding_complete');
    const stripeUpdateComplete = queryParams.get('stripe_update_complete');

    // If user has returned from Stripe onboarding, check their onboarding status
    if (stripeOnboardingComplete === 'true') {
      checkOnboardingStatus();

      // Remove the query parameter from the URL without triggering a navigation
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // If user has returned from updating their Stripe account, we can show a success message
    if (stripeUpdateComplete === 'true') {
      setBankDetailsMessage({
        text: 'Your bank details have been successfully updated.',
        type: 'success'
      });

      // Remove the query parameter from the URL without triggering a navigation
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  const checkStripeAccount = async () => {
    try {
      console.log('Checking if user has Stripe account...');
      const response = await fetchWithToken('/check-stripe-account', {
        method: 'POST',
      });

      const data = await response.json();

      if (data.hasStripeAccount === true) {
        setHasStripeAccount(true);
      } else {
        console.log('User has not created Stripe account');
        setHasStripeAccount(false);
      }
    } catch (error) {
      console.error('Error checking if user has Stripe account:', error);
    }
  };

  useEffect(() => {
    if (hasStripeAccount === null) {
      checkStripeAccount();
    }
  }, []);

  // Function to check onboarding status and update context
  const checkOnboardingStatus = async () => {
    try {
      const response = await fetchWithToken('/check-stripe-onboarding', {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.onboardingComplete) {
          setStripeOnboardingStatus('complete');
          setBankDetailsMessage({
            text: 'Stripe onboarding completed successfully. You can now receive payments!',
            type: 'success'
          });
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Use the new getUserData function to fetch user profile data
        const userData = await getUserData(['displayName', 'email', 'linkId', 'icon', 'stripeOnboardingStatus', 'country', 'signupMethod']);

        console.log('User data retrieved in Settings: ', userData);
        console.log('Link ID retrieved in Settings: ', linkId);
        setDisplayName(userData.displayName || '');
        setEmail(userData.email || '');
        setIcon(userData.icon || '');
        setLinkId(userData.linkId || '');

        setOriginalDisplayName(userData.displayName || '');
        setOriginalEmail(userData.email || '');
        setOriginalIcon(userData.icon || '');
        setOriginalLinkId(userData.linkId || '');

        setStripeOnboardingStatus(userData.stripeOnboardingStatus);
        setUserCountry(userData.country);
        setSignupMethod(userData.signupMethod);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setProfileMessage({ text: 'Failed to load user data', type: 'error' });
      }
    };

    fetchUserData();
  }, []);



  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ text: 'New passwords do not match', type: 'error' });
      return;
    }

    setIsLoading(true);
    setPasswordMessage({ text: '', type: '' });

    try {
      const response = await fetchWithToken('/update-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({ text: 'Password updated successfully', type: 'success' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordMessage({ text: data.message || 'Failed to update password', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordMessage({ text: 'An error occurred while updating your password', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateBankDetails = async () => {
    setIsLoadingBankDetails(true);
    setBankDetailsMessage({ text: '', type: '' });

    try {
      const response = await fetchWithToken('/create-stripe-update-link', {
        method: 'POST',
        body: JSON.stringify({
          return_url: `${window.location.origin}/settings?stripe_update_complete=true`,
          refresh_url: `${window.location.origin}/settings?stripe_update_canceled=true`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create update link');
      }

      const data = await response.json();

      if (data.accountLink && data.accountLink.url) {
        // Open the Stripe update link in a new tab
        window.open(data.accountLink.url, '_blank');
        setBankDetailsMessage({
          text: 'Stripe dashboard opened in a new tab. Please complete the process there.',
          type: 'success'
        });
      } else {
        setBankDetailsMessage({
          text: 'Unable to create update link. Please try again later.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating bank details update link:', error);
      setBankDetailsMessage({
        text: 'An error occurred while trying to update bank details',
        type: 'error'
      });
    } finally {
      setIsLoadingBankDetails(false);
    }
  };

  const handleStartStripeOnboarding = async () => {
    setIsLoadingBankDetails(true);
    setBankDetailsMessage({ text: '', type: '' });

    try {
      // First create a Stripe account if the user doesn't have one
      if (!hasStripeAccount) {
        const createAccountResponse = await fetchWithToken('/create-stripe-account', {
          method: 'POST',
          body: JSON.stringify({ country: userCountry })
        });

        if (!createAccountResponse.ok) {
          throw new Error('Failed to create Stripe account');
        }
      }

      // Then create an onboarding link
      const response = await fetchWithToken('/create-stripe-onboarding-link', {
        method: 'POST',
        body: JSON.stringify({
          return_url: `${window.location.origin}/settings?stripe_onboarding_complete=true`,
          refresh_url: `${window.location.origin}/settings?stripe_onboarding_canceled=true`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create onboarding link');
      }

      const data = await response.json();

      if (data.accountLink && data.accountLink.url) {
        // Open the Stripe onboarding link in a new tab
        window.open(data.accountLink.url, '_blank');
        setBankDetailsMessage({
          text: 'Stripe onboarding opened in a new tab. Please complete the process there.',
          type: 'success'
        });
      } else {
        setBankDetailsMessage({
          text: 'Unable to create onboarding link. Please try again later.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error starting Stripe onboarding:', error);
      setBankDetailsMessage({
        text: 'An error occurred while trying to set up payments',
        type: 'error'
      });
    } finally {
      setIsLoadingBankDetails(false);
    }
  };

  return (
    <div className="flex md:min-h-[calc(100vh-3rem)] items-center">
      {/* Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      {/* Main content */}
      <div className="h-full w-full max-w-4xl mx-auto p-4 relative min-[768px]:ml-48 min-[1250px]:ml-auto md:flex md:items-center">
        <div className={`max-w-6xl mx-auto pt-12 ${signupMethod === 'google' ? 'h-full flex flex-col' : ''}`}>
          {/* <h1 className="text-2xl font-bold text-white mb-6">Account Settings</h1> */}

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${signupMethod === 'google' ? 'md:items-stretch md:flex-1' : ''}`}>
            {/* Profile Information - Left column */}
            <section aria-labelledby="profile-heading" className={`md:max-w-[500px] ${signupMethod === 'google' ? 'md:h-full' : ''}`}>
              <ProfileSection
                displayName={displayName}
                setDisplayName={setDisplayName}
                email={email}
                setEmail={setEmail}
                icon={icon}
                setIcon={setIcon}
                linkId={linkId}
                setLinkId={setLinkId}
                originalIcon={originalIcon}
                setOriginalIcon={setOriginalIcon}
                setUserIcon={setUserIcon}
                iconFile={iconFile}
                setIconFile={setIconFile}
                originalDisplayName={originalDisplayName}
                setOriginalDisplayName={setOriginalDisplayName}
                originalLinkId={originalLinkId}
                setOriginalLinkId={setOriginalLinkId}
                originalEmail={originalEmail}
                setOriginalEmail={setOriginalEmail}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                profileMessage={profileMessage}
                setProfileMessage={setProfileMessage}
                signupMethod={signupMethod}
              />
            </section>

            {/* Right column */}
            <div className={`flex flex-col gap-6 md:gap-0 md:max-w-[500px] ${signupMethod === 'google' ? 'md:h-full' : 'md:justify-between'}`}>
              {/* Password Update - only show for non-Google signup */}
              {signupMethod !== 'google' && (
                <section aria-labelledby="password-heading">
                  <PasswordSection
                    currentPassword={currentPassword}
                    setCurrentPassword={setCurrentPassword}
                    newPassword={newPassword}
                    setNewPassword={setNewPassword}
                    confirmPassword={confirmPassword}
                    setConfirmPassword={setConfirmPassword}
                    passwordMessage={passwordMessage}
                    isLoading={isLoading}
                    handlePasswordUpdate={handlePasswordUpdate}
                  />
                </section>
              )}

              {/* Bank Details Update Section */}
              <section aria-labelledby="bank-details-heading" className={signupMethod === 'google' ? 'md:h-full' : 'pt-4'}>
                <BankDetailsSection
                  stripeOnboardingStatus={stripeOnboardingStatus}
                  bankDetailsMessage={bankDetailsMessage}
                  isLoadingBankDetails={isLoadingBankDetails}
                  handleUpdateBankDetails={handleUpdateBankDetails}
                  handleStartStripeOnboarding={handleStartStripeOnboarding}
                  signupMethod={signupMethod}
                />
              </section>
            </div>

            {/* Delete Account Section */}
            <section className='md:col-span-2' aria-labelledby="delete-account-heading">
              <div className="relative mx-auto w-full">
                <DeleteAccountSection />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
